import {
  TableCellType,
  TableHeader,
} from "../../../../../components/custom-table/table";
import { EditableTagListCellValue } from "../../../../../components/custom-table/tableCell/cellTypes";
import { BadgeSelectCellValue } from "../../../../../components/custom-table/tableCell/cellTypes/BadgeSelectCell";
import {
  FilterSection,
  FilterValues,
} from "../../../../../data-model/filterValues";
import {
  EMPTY_FILTERS,
  MedicationClass,
  MedicationCohort,
  MedicationListFilters,
  MedicationListItem,
  MedicationSource,
} from "../data-model/medicationListModel";
import {
  CLASS_BADGE_LIST,
  CLASS_OPTIONS,
  COHORT_BADGE_LIST,
  COHORT_OPTIONS,
  SOURCE_OPTIONS,
} from "./medicationListConstants";

/**
 * Column keys are intentionally identical to the underlying medication field
 * names — that keeps the onCellValueChange dispatch a single line in the page
 * (`updateRow(name, { [fieldKey]: newValue })`).
 *
 * Order mirrors the Figma reference and is preserved by `order` so a future
 * editor can reorder via drag-and-drop without touching the data model.
 */
export const buildTableHeaders = (): TableHeader[] => [
  {
    key: "drug",
    label: "Drug",
    order: 0,
    width: 14,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    key: "class",
    label: "Generic / Brand",
    order: 1,
    width: 12,
    type: TableCellType.SELECTABLE_BADGE,
  },
  {
    key: "cohort",
    label: "Cohort",
    order: 2,
    width: 10,
    type: TableCellType.SELECTABLE_BADGE,
  },
  {
    key: "related_drugs",
    label: "Related Drugs",
    order: 3,
    width: 20,
    type: TableCellType.EDITABLE_TAG_LIST,
  },
  {
    key: "name_collision_exclusions",
    label: "Presc Collision List",
    order: 4,
    width: 16,
    type: TableCellType.EDITABLE_TAG_LIST,
  },
  {
    key: "name_replacement",
    label: "Prescription Replacement",
    order: 5,
    width: 16,
    type: TableCellType.EDITABLE_TAG_LIST,
  },
  {
    key: "replacement_name",
    label: "Medication Replacement",
    order: 6,
    width: 14,
    type: TableCellType.EDITABLE_TAG_LIST,
  },
];

export interface MedicationTableRow {
  /** Lookup key — also passed back as `rowData.name` to the cell change handler. */
  name: string;
  isDirty: boolean;
  drug: string;
  class: BadgeSelectCellValue;
  cohort: BadgeSelectCellValue;
  related_drugs: EditableTagListCellValue;
  name_collision_exclusions: EditableTagListCellValue;
  name_replacement: EditableTagListCellValue;
  replacement_name: EditableTagListCellValue;
}

export const buildTableRows = (
  medications: MedicationListItem[],
  isDirty: (name: string) => boolean,
): MedicationTableRow[] =>
  medications.map((m) => ({
    name: m.name,
    isDirty: isDirty(m.name),
    drug: m.display_name,
    class: {
      selectedBadge:
        CLASS_BADGE_LIST.find((b) => b.id === (m.class || "Unknown")) ??
        CLASS_BADGE_LIST[CLASS_BADGE_LIST.length - 1],
      badgeList: CLASS_BADGE_LIST,
    },
    cohort: {
      selectedBadge:
        COHORT_BADGE_LIST.find((b) => b.id === (m.cohort || "Unknown")) ??
        COHORT_BADGE_LIST[COHORT_BADGE_LIST.length - 1],
      badgeList: COHORT_BADGE_LIST,
    },
    related_drugs: {
      values: m.related_drugs || [],
      placeholder: "Drug",
    },
    name_collision_exclusions: {
      values: m.name_collision_exclusions || [],
      placeholder: "Exclude",
    },
    name_replacement: {
      values: m.name_replacement ? [m.name_replacement] : [],
      placeholder: "Name",
    },
    replacement_name: {
      values: m.replacement_name ? [m.replacement_name] : [],
      placeholder: "Name",
    },
  }));

/**
 * FilterSection list driving CustomTable's built-in filter UI. Section ids must
 * match the medication field names so `filterValuesToMedicationFilters` can
 * map them back without a hand-coded switch.
 */
export const buildFilterSections = (): FilterSection[] => [
  {
    id: "class",
    label: "Generic / Brand",
    type: "string",
    options: CLASS_OPTIONS,
  },
  {
    id: "cohort",
    label: "Cohort",
    type: "string",
    options: COHORT_OPTIONS,
  },
  {
    id: "source",
    label: "Source",
    type: "string",
    options: SOURCE_OPTIONS,
  },
];

const SECTION_TO_FIELD: Record<keyof MedicationListFilters, string> = {
  classes: "class",
  cohorts: "cohort",
  sources: "source",
};

export const filterValuesToMedicationFilters = (
  data: FilterValues | undefined,
): MedicationListFilters => {
  if (!data?.values) return EMPTY_FILTERS;
  const next: MedicationListFilters = {
    classes:
      (data.values[SECTION_TO_FIELD.classes]?.values as MedicationClass[]) ||
      [],
    cohorts:
      (data.values[SECTION_TO_FIELD.cohorts]?.values as MedicationCohort[]) ||
      [],
    sources:
      (data.values[SECTION_TO_FIELD.sources]?.values as MedicationSource[]) ||
      [],
  };
  return next;
};
